import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Database, Play, Square, RefreshCw, Trash2, Plus,
  AlertTriangle, Terminal, Table2, 
  FileText, Code, Download, Info, Loader2,
  Server, Globe, Settings, Star, StarOff, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { ScrollArea } from './ui/scroll-area';
import apiService from '../services/api';

const KsqlDBManager = ({ clusterId, cluster, credentials }) => {
  const [ksqldb, setKsqldb] = useState(null);
  const [ksqldbInstances, setKsqldbInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Install dialog
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [installationType, setInstallationType] = useState('broker'); // 'broker' | 'separate' | 'existing'
  const [installNode, setInstallNode] = useState('');
  const [separateNodeHost, setSeparateNodeHost] = useState('');
  const [existingKsqldbUrl, setExistingKsqldbUrl] = useState('');
  const [instanceName, setInstanceName] = useState('default');
  const [installPort, setInstallPort] = useState('8088');
  const [installPath, setInstallPath] = useState('/opt/ksqldb');
  const [installHeap, setInstallHeap] = useState('1G');
  const [customDownloadUrl, setCustomDownloadUrl] = useState('');
  const [installing, setInstalling] = useState(false);
  const [installLogs, setInstallLogs] = useState([]);
  
  // Query state
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingResults, setStreamingResults] = useState([]);
  const [streamingSchema, setStreamingSchema] = useState(null);
  const streamEventSourceRef = useRef(null);
  
  // Objects state
  const [streams, setStreams] = useState([]);
  const [tables, setTables] = useState([]);
  const [queries, setQueries] = useState([]);
  const [serverInfo, setServerInfo] = useState(null);
  
  // Create stream/table dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createType, setCreateType] = useState('STREAM');
  const [createDDL, setCreateDDL] = useState('');

  const loadKsqlDBInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getKsqlDBInfo(clusterId);
      if (response.success) {
        setKsqldb(response.ksqldb);
        setKsqldbInstances(response.ksqldbInstances || []);
      }
    } catch (err) {
      console.error('Failed to load ksqlDB info:', err);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  const loadObjects = useCallback(async () => {
    try {
      const [streamsRes, tablesRes, queriesRes, infoRes] = await Promise.all([
        apiService.getKsqlDBStreams(clusterId).catch(() => ({ streams: [] })),
        apiService.getKsqlDBTables(clusterId).catch(() => ({ tables: [] })),
        apiService.getKsqlDBQueries(clusterId).catch(() => ({ queries: [] })),
        apiService.getKsqlDBServerInfo(clusterId).catch(() => ({ info: null }))
      ]);
      
      setStreams(streamsRes.streams || []);
      setTables(tablesRes.tables || []);
      setQueries(queriesRes.queries || []);
      setServerInfo(infoRes.info || null);
    } catch (err) {
      console.error('Failed to load ksqlDB objects:', err);
    }
  }, [clusterId]);

  useEffect(() => {
    loadKsqlDBInfo();
  }, [loadKsqlDBInfo]);

  useEffect(() => {
    if (ksqldb?.status === 'running') {
      loadObjects();
    }
  }, [ksqldb, loadObjects]);

  const handleInstall = async () => {
    setInstalling(true);
    setInstallLogs([]);
    setError(null);
    
    try {
      let nodeHost = '';
      
      if (installationType === 'broker') {
        if (!installNode) {
          setError('Please select a broker node');
          return;
        }
        nodeHost = installNode;
      } else if (installationType === 'separate') {
        if (!separateNodeHost) {
          setError('Please enter the separate node hostname/IP');
          return;
        }
        nodeHost = separateNodeHost;
      } else if (installationType === 'existing') {
        if (!existingKsqldbUrl) {
          setError('Please enter the existing ksqlDB URL');
          return;
        }
      }
      
      if (installationType !== 'existing' && !credentials?.username) {
        setError('SSH credentials are required for installation');
        return;
      }
      
      const response = await apiService.installKsqlDB(clusterId, {
        node: installationType !== 'existing' ? { host: nodeHost } : null,
        credentials,
        ksqldbPath: installPath,
        port: parseInt(installPort),
        heapSize: installHeap,
        customDownloadUrl: customDownloadUrl || null,
        installationType,
        existingKsqldbUrl: installationType === 'existing' ? existingKsqldbUrl : null,
        instanceName: instanceName || 'default'
      });
      
      if (response.success) {
        setInstallLogs(response.logs || []);
        setKsqldb(response.ksqldb);
        setSuccess(`ksqlDB ${installationType === 'existing' ? 'connected' : 'installed'} successfully!`);
        setShowInstallDialog(false);
        loadKsqlDBInfo();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setInstalling(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleUninstall = async (instanceNameToRemove) => {
    const instance = ksqldbInstances.find(i => i.name === instanceNameToRemove) || ksqldb;
    const confirmMsg = instance?.type === 'existing' 
      ? `Remove connection to ${instanceNameToRemove}?`
      : `Uninstall ksqlDB instance "${instanceNameToRemove}"? This will remove all data.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const response = await apiService.uninstallKsqlDB(clusterId, credentials, instanceNameToRemove);
      if (response.success) {
        setSuccess(response.message);
        loadKsqlDBInfo();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    }
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleAction = async (action, instanceNameForAction) => {
    try {
      const response = await apiService.ksqlDBAction(clusterId, action, credentials, instanceNameForAction);
      if (response.success) {
        setSuccess(`ksqlDB ${action} successful`);
        loadKsqlDBInfo();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    }
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSetPrimary = async (instanceNameToSet) => {
    try {
      const response = await apiService.setKsqlDBPrimary(clusterId, instanceNameToSet);
      if (response.success) {
        setSuccess(`${instanceNameToSet} set as primary`);
        loadKsqlDBInfo();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    }
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleUpdateConfig = async (instanceNameToUpdate) => {
    try {
      const response = await apiService.updateKsqlDBConfig(clusterId, credentials, instanceNameToUpdate);
      if (response.success) {
        setSuccess('Configuration updated. ksqlDB restarted.');
        loadKsqlDBInfo();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    }
    setTimeout(() => setSuccess(null), 3000);
  };

  const stopStreaming = useCallback(() => {
    if (streamEventSourceRef.current) {
      streamEventSourceRef.current.abort();
      streamEventSourceRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const isPushQuery = (q) => {
    const upperQuery = q.toUpperCase();
    return upperQuery.includes('EMIT CHANGES') || 
           (upperQuery.includes('SELECT') && !upperQuery.includes('CREATE') && !upperQuery.includes('INSERT'));
  };

  const handleExecuteQuery = async () => {
    if (!query.trim()) return;
    
    stopStreaming();
    setExecuting(true);
    setQueryResult(null);
    setStreamingResults([]);
    setStreamingSchema(null);
    setError(null);
    
    try {
      if (isPushQuery(query)) {
        setIsStreaming(true);
        try {
          const response = await apiService.ksqlDBPushQuery(clusterId, query, {}, 15000);
          if (response.success) {
            setStreamingSchema(response.schema);
            setStreamingResults(response.results || []);
          } else {
            setError(response.message);
          }
        } catch (err) {
          if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
            setError('Query timed out. Add a LIMIT clause for bounded results.');
          } else {
            setError(err.message);
          }
        } finally {
          setIsStreaming(false);
        }
      } else {
        const response = await apiService.ksqlDBQuery(clusterId, query);
        if (response.success) {
          setQueryResult(response.results);
          if (query.toUpperCase().match(/^(CREATE|DROP|TERMINATE)/)) {
            loadObjects();
          }
        } else {
          setError(response.message);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleTerminateQuery = async (queryId) => {
    if (!window.confirm(`Terminate query ${queryId}?`)) return;
    try {
      const response = await apiService.terminateKsqlDBQuery(clusterId, queryId);
      if (response.success) {
        loadObjects();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDropObject = async (type, name) => {
    const deleteTopic = window.confirm(`Delete underlying topic for ${name}?`);
    try {
      const response = await apiService.dropKsqlDBObject(clusterId, type, name, deleteTopic);
      if (response.success) {
        loadObjects();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const renderQueryResult = () => {
    if (!queryResult) return null;
    
    return (
      <div className="mt-4 space-y-2">
        {queryResult.map((result, index) => (
          <Card key={index} className="p-3">
            {result['@type'] && (
              <Badge variant="outline" className="mb-2">{result['@type']}</Badge>
            )}
            {result.commandStatus && (
              <div className="text-sm">
                <span className={result.commandStatus.status === 'SUCCESS' ? 'text-green-500' : 'text-red-500'}>
                  {result.commandStatus.status}
                </span>
                {result.commandStatus.message && (
                  <p className="text-xs text-muted-foreground mt-1">{result.commandStatus.message}</p>
                )}
              </div>
            )}
            {result.sourceDescription && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{result.sourceDescription.name}</p>
                <p className="text-xs text-muted-foreground">{result.sourceDescription.type}</p>
                {result.sourceDescription.fields && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Field</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.sourceDescription.fields.map((field, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{field.name}</TableCell>
                          <TableCell className="text-xs font-mono">{field.schema?.type}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2">Loading ksqlDB info...</span>
        </CardContent>
      </Card>
    );
  }

  // Not installed view
  if (!ksqldb && ksqldbInstances.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-5 w-5 text-primary" />
            ksqlDB
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Database className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">ksqlDB Not Configured</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Install ksqlDB or connect to an existing cluster for stream processing
            </p>
            <Button onClick={() => setShowInstallDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add ksqlDB
            </Button>
          </div>
          
          {/* Install Dialog */}
          <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>Add ksqlDB</DialogTitle>
                <DialogDescription>
                  Install ksqlDB on a server or connect to an existing cluster
                </DialogDescription>
              </DialogHeader>
              
              {error && (
                <Alert variant="destructive" className="flex-shrink-0">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
              <div className="space-y-4">
                {/* Installation Type */}
                <div className="space-y-3">
                  <Label>Installation Type</Label>
                  <RadioGroup value={installationType} onValueChange={setInstallationType}>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="broker" id="broker" />
                      <Label htmlFor="broker" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Server className="h-4 w-4 text-primary" />
                          <span className="font-medium">Install on Broker Node</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Install on one of your existing Kafka broker nodes
                        </p>
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="separate" id="separate" />
                      <Label htmlFor="separate" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Server className="h-4 w-4 text-blue-500" />
                          <span className="font-medium">Install on Separate Node</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Install on a dedicated Linux instance (better isolation)
                        </p>
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="existing" id="existing" />
                      <Label htmlFor="existing" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-green-500" />
                          <span className="font-medium">Connect to Existing Cluster</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Connect to an already running ksqlDB cluster
                        </p>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                {/* Instance Name */}
                <div className="space-y-2">
                  <Label>Instance Name</Label>
                  <Input 
                    value={instanceName} 
                    onChange={(e) => setInstanceName(e.target.value)}
                    placeholder="default"
                  />
                  <p className="text-xs text-muted-foreground">
                    A unique name for this ksqlDB instance
                  </p>
                </div>
                
                {/* Broker Node Selection */}
                {installationType === 'broker' && (
                  <div className="space-y-2">
                    <Label>Target Broker Node</Label>
                    <Select value={installNode} onValueChange={setInstallNode}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a broker" />
                      </SelectTrigger>
                      <SelectContent>
                        {cluster?.nodes?.map((node, i) => (
                          <SelectItem key={i} value={node.host}>
                            {node.host} {node.privateIp && `(${node.privateIp})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Separate Node Input */}
                {installationType === 'separate' && (
                  <div className="space-y-2">
                    <Label>Separate Node Hostname/IP</Label>
                    <Input 
                      value={separateNodeHost} 
                      onChange={(e) => setSeparateNodeHost(e.target.value)}
                      placeholder="192.168.1.100 or ksqldb.example.com"
                    />
                    <p className="text-xs text-muted-foreground">
                      SSH credentials will be used to connect and install
                    </p>
                  </div>
                )}
                
                {/* Existing ksqlDB URL */}
                {installationType === 'existing' && (
                  <div className="space-y-2">
                    <Label>ksqlDB REST API URL</Label>
                    <Input 
                      value={existingKsqldbUrl} 
                      onChange={(e) => setExistingKsqldbUrl(e.target.value)}
                      placeholder="http://ksqldb-server:8088"
                    />
                    <p className="text-xs text-muted-foreground">
                      URL to an existing ksqlDB REST API endpoint
                    </p>
                  </div>
                )}
                
                {/* Installation Options (not for existing) */}
                {installationType !== 'existing' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Port</Label>
                        <Input 
                          value={installPort} 
                          onChange={(e) => setInstallPort(e.target.value)}
                          placeholder="8088"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Heap Size</Label>
                        <Input 
                          value={installHeap} 
                          onChange={(e) => setInstallHeap(e.target.value)}
                          placeholder="1G"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Install Path</Label>
                      <Input 
                        value={installPath} 
                        onChange={(e) => setInstallPath(e.target.value)}
                        placeholder="/opt/ksqldb"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Custom Download URL (Optional)</Label>
                      <Input 
                        value={customDownloadUrl} 
                        onChange={(e) => setCustomDownloadUrl(e.target.value)}
                        placeholder="https://..."
                        className="font-mono text-xs"
                      />
                      <p className="text-xs text-muted-foreground">
                        Leave empty to download from Confluent
                      </p>
                    </div>
                  </>
                )}
                
                {installLogs.length > 0 && (
                  <ScrollArea className="h-32 border rounded p-2 bg-muted/50">
                    {installLogs.map((log, i) => (
                      <p key={i} className="text-xs font-mono">{log}</p>
                    ))}
                  </ScrollArea>
                )}
              </div>
              </div>
              
              <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowInstallDialog(false)} disabled={installing}>
                  Cancel
                </Button>
                <Button onClick={handleInstall} disabled={installing}>
                  {installing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {installationType === 'existing' ? 'Connecting...' : 'Installing...'}
                    </>
                  ) : (
                    <>
                      {installationType === 'existing' ? (
                        <><Globe className="h-4 w-4 mr-2" />Connect</>
                      ) : (
                        <><Download className="h-4 w-4 mr-2" />Install</>
                      )}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  // Installed view
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-5 w-5 text-primary" />
            ksqlDB
            {ksqldb && (
              <Badge variant={ksqldb.status === 'running' ? 'success' : 'secondary'}>
                {ksqldb.status}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowInstallDialog(true)}>
              <Plus className="h-3 w-3 mr-1" />Add Instance
            </Button>
            {ksqldb?.type !== 'existing' && (
              <>
                {ksqldb?.status === 'running' ? (
                  <Button variant="outline" size="sm" onClick={() => handleAction('stop', ksqldb?.name)}>
                    <Square className="h-3 w-3 mr-1" />Stop
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => handleAction('start', ksqldb?.name)}>
                    <Play className="h-3 w-3 mr-1" />Start
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => handleAction('restart', ksqldb?.name)}>
                  <RefreshCw className="h-3 w-3 mr-1" />Restart
                </Button>
              </>
            )}
          </div>
        </div>
        {ksqldb && (
          <p className="text-xs text-muted-foreground">
            Primary: {ksqldb.host}:{ksqldb.port} 
            {ksqldb.type === 'existing' && ' (External)'}
            {ksqldb.path && ` • ${ksqldb.path}`}
          </p>
        )}
      </CardHeader>
      
      <CardContent className="pt-0">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="mb-4 border-green-500/50 bg-green-500/10">
            <AlertDescription className="text-green-600">{success}</AlertDescription>
          </Alert>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview" className="text-xs">
              <Info className="h-3 w-3 mr-1" />Overview
            </TabsTrigger>
            <TabsTrigger value="instances" className="text-xs">
              <Server className="h-3 w-3 mr-1" />Instances ({ksqldbInstances.length || 1})
            </TabsTrigger>
            <TabsTrigger value="query" className="text-xs">
              <Terminal className="h-3 w-3 mr-1" />Query Editor
            </TabsTrigger>
            <TabsTrigger value="streams" className="text-xs">
              <FileText className="h-3 w-3 mr-1" />Streams ({streams.length})
            </TabsTrigger>
            <TabsTrigger value="tables" className="text-xs">
              <Table2 className="h-3 w-3 mr-1" />Tables ({tables.length})
            </TabsTrigger>
            <TabsTrigger value="queries" className="text-xs">
              <Code className="h-3 w-3 mr-1" />Queries ({queries.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4">
                <h4 className="text-sm font-semibold mb-2">Server Info</h4>
                {serverInfo ? (
                  <div className="space-y-1 text-xs">
                    <p><span className="text-muted-foreground">Version:</span> {serverInfo.version}</p>
                    <p><span className="text-muted-foreground">Kafka Cluster:</span> {serverInfo.kafkaClusterId}</p>
                    <p><span className="text-muted-foreground">Service ID:</span> {serverInfo.ksqlServiceId}</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Unable to fetch server info</p>
                )}
              </Card>
              
              <Card className="p-4">
                <h4 className="text-sm font-semibold mb-2">Statistics</h4>
                <div className="space-y-1 text-xs">
                  <p><span className="text-muted-foreground">Streams:</span> {streams.length}</p>
                  <p><span className="text-muted-foreground">Tables:</span> {tables.length}</p>
                  <p><span className="text-muted-foreground">Running Queries:</span> {queries.length}</p>
                  <p><span className="text-muted-foreground">Instances:</span> {ksqldbInstances.length || 1}</p>
                </div>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="instances">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-semibold">ksqlDB Instances</h4>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Host</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(ksqldbInstances.length > 0 ? ksqldbInstances : [ksqldb]).filter(Boolean).map((instance, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {instance.name === ksqldb?.name && (
                            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                          )}
                          {instance.name}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {instance.host}:{instance.port}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {instance.type === 'existing' ? 'External' : 
                           instance.type === 'separate' ? 'Dedicated' : 'Broker'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={instance.status === 'running' ? 'success' : 'secondary'}>
                          {instance.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {instance.name !== ksqldb?.name && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetPrimary(instance.name)}
                              title="Set as primary"
                            >
                              <StarOff className="h-3 w-3" />
                            </Button>
                          )}
                          {instance.type !== 'existing' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateConfig(instance.name)}
                              title="Update configuration"
                            >
                              <Settings className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleUninstall(instance.name)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {cluster?.security && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Security Note:</strong> ksqlDB is configured to connect to Kafka using {cluster.security} protocol.
                    {cluster.security.includes('SASL') && ' SASL credentials are automatically configured.'}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="query">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>ksqlDB Query</Label>
                <Textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter ksqlDB statement (e.g., LIST STREAMS; or CREATE STREAM ...)"
                  className="font-mono text-sm h-32"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={handleExecuteQuery} disabled={executing || !query.trim()}>
                  {executing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Executing...</>
                  ) : (
                    <><Play className="h-4 w-4 mr-2" />Execute</>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setQuery('LIST STREAMS;')}>
                  List Streams
                </Button>
                <Button variant="outline" onClick={() => setQuery('LIST TABLES;')}>
                  List Tables
                </Button>
                <Button variant="outline" onClick={() => setQuery('LIST QUERIES;')}>
                  List Queries
                </Button>
                <Button variant="outline" onClick={() => setQuery('SHOW PROPERTIES;')}>
                  Properties
                </Button>
              </div>
              
              {(streamingSchema || streamingResults.length > 0) && (
                <Card className="mt-4 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={isStreaming ? 'default' : 'secondary'}>
                      {isStreaming ? 'Streaming...' : `${streamingResults.length} Results`}
                    </Badge>
                    {isStreaming && (
                      <Button variant="destructive" size="sm" onClick={stopStreaming}>
                        <Square className="h-3 w-3 mr-1" />Stop
                      </Button>
                    )}
                  </div>
                  
                  {streamingSchema && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {streamingSchema.columnNames?.map((col, i) => (
                              <TableHead key={i} className="text-xs">
                                {col}
                                <span className="text-muted-foreground ml-1">({streamingSchema.columnTypes?.[i]})</span>
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {streamingResults.slice(-50).map((row, i) => (
                            <TableRow key={i}>
                              {(Array.isArray(row) ? row : row.columns || []).map((cell, j) => (
                                <TableCell key={j} className="text-xs font-mono">
                                  {typeof cell === 'object' ? JSON.stringify(cell) : String(cell)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </Card>
              )}
              
              {renderQueryResult()}
            </div>
          </TabsContent>
          
          <TabsContent value="streams">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-semibold">Streams</h4>
                <Button size="sm" onClick={() => {
                  setCreateType('STREAM');
                  setCreateDDL('CREATE STREAM stream_name (\n  column1 VARCHAR,\n  column2 INT\n) WITH (\n  KAFKA_TOPIC=\'topic_name\',\n  VALUE_FORMAT=\'JSON\'\n);');
                  setShowCreateDialog(true);
                }}>
                  <Plus className="h-3 w-3 mr-1" />Create Stream
                </Button>
              </div>
              
              {streams.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No streams found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Topic</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {streams.map((stream, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{stream.name}</TableCell>
                        <TableCell className="text-xs font-mono">{stream.topic}</TableCell>
                        <TableCell><Badge variant="outline">{stream.format || 'JSON'}</Badge></TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDropObject('STREAM', stream.name)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="tables">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-semibold">Tables</h4>
                <Button size="sm" onClick={() => {
                  setCreateType('TABLE');
                  setCreateDDL('CREATE TABLE table_name (\n  key_column VARCHAR PRIMARY KEY,\n  column1 VARCHAR\n) WITH (\n  KAFKA_TOPIC=\'topic_name\',\n  VALUE_FORMAT=\'JSON\'\n);');
                  setShowCreateDialog(true);
                }}>
                  <Plus className="h-3 w-3 mr-1" />Create Table
                </Button>
              </div>
              
              {tables.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No tables found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Topic</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tables.map((table, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{table.name}</TableCell>
                        <TableCell className="text-xs font-mono">{table.topic}</TableCell>
                        <TableCell><Badge variant="outline">{table.format || 'JSON'}</Badge></TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDropObject('TABLE', table.name)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="queries">
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Running Queries</h4>
              
              {queries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No running queries</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Query ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Sink</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {queries.map((q, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{q.id || q.queryString?.substring(0, 50)}</TableCell>
                        <TableCell><Badge variant="outline">{q.queryType || 'PERSISTENT'}</Badge></TableCell>
                        <TableCell className="text-xs">{q.sinks?.join(', ') || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTerminateQuery(q.id)}
                          >
                            <Square className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Create Stream/Table Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create {createType}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea
                value={createDDL}
                onChange={(e) => setCreateDDL(e.target.value)}
                className="font-mono text-sm h-48"
                placeholder={`CREATE ${createType} ...`}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={() => {
                setQuery(createDDL);
                setShowCreateDialog(false);
                setActiveTab('query');
              }}>
                <Code className="h-4 w-4 mr-2" />
                Open in Editor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Install Dialog for adding more instances */}
        <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Add ksqlDB Instance</DialogTitle>
              <DialogDescription>
                Add another ksqlDB instance or connect to an external cluster
              </DialogDescription>
            </DialogHeader>
            
            {error && (
              <Alert variant="destructive" className="flex-shrink-0">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="flex-1 overflow-y-auto pr-2 min-h-0">
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>Installation Type</Label>
                <RadioGroup value={installationType} onValueChange={setInstallationType}>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                    <RadioGroupItem value="broker" id="broker2" />
                    <Label htmlFor="broker2" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        <span>Install on Broker Node</span>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                    <RadioGroupItem value="separate" id="separate2" />
                    <Label htmlFor="separate2" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-blue-500" />
                        <span>Install on Separate Node</span>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50">
                    <RadioGroupItem value="existing" id="existing2" />
                    <Label htmlFor="existing2" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-green-500" />
                        <span>Connect to Existing</span>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              <div className="space-y-2">
                <Label>Instance Name</Label>
                <Input 
                  value={instanceName} 
                  onChange={(e) => setInstanceName(e.target.value)}
                  placeholder="ksqldb-2"
                />
              </div>
              
              {installationType === 'broker' && (
                <div className="space-y-2">
                  <Label>Target Broker Node</Label>
                  <Select value={installNode} onValueChange={setInstallNode}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a broker" />
                    </SelectTrigger>
                    <SelectContent>
                      {cluster?.nodes?.map((node, i) => (
                        <SelectItem key={i} value={node.host}>
                          {node.host}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {installationType === 'separate' && (
                <div className="space-y-2">
                  <Label>Separate Node Hostname/IP</Label>
                  <Input 
                    value={separateNodeHost} 
                    onChange={(e) => setSeparateNodeHost(e.target.value)}
                    placeholder="192.168.1.100"
                  />
                </div>
              )}
              
              {installationType === 'existing' && (
                <div className="space-y-2">
                  <Label>ksqlDB REST API URL</Label>
                  <Input 
                    value={existingKsqldbUrl} 
                    onChange={(e) => setExistingKsqldbUrl(e.target.value)}
                    placeholder="http://ksqldb-server:8088"
                  />
                </div>
              )}
              
              {installationType !== 'existing' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input value={installPort} onChange={(e) => setInstallPort(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Heap Size</Label>
                    <Input value={installHeap} onChange={(e) => setInstallHeap(e.target.value)} />
                  </div>
                </div>
              )}
              
              {installLogs.length > 0 && (
                <ScrollArea className="h-32 border rounded p-2 bg-muted/50">
                  {installLogs.map((log, i) => (
                    <p key={i} className="text-xs font-mono">{log}</p>
                  ))}
                </ScrollArea>
              )}
            </div>
            </div>
            
            <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowInstallDialog(false)} disabled={installing}>
                Cancel
              </Button>
              <Button onClick={handleInstall} disabled={installing}>
                {installing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Working...</>
                ) : installationType === 'existing' ? (
                  <><Globe className="h-4 w-4 mr-2" />Connect</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" />Install</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default KsqlDBManager;
